import { Type, type Static } from "@sinclair/typebox";
const uuid = Type.String({ format: "uuid" });
const year = Type.Integer({ minimum: 2000, maximum: 2200 });
const amount = Type.String({ pattern: "^[0-9]+(?:\\.[0-9]{1,2})?$" });
export const StructureInvoiceRouteSchema = Type.Object(
  {
    kind: Type.Union([Type.Literal("supplier"), Type.Literal("category")]),
    key: Type.String({ minLength: 1, maxLength: 100 }),
    costCenterId: uuid,
  },
  { additionalProperties: false },
);
export type StructureInvoiceRoute = Static<typeof StructureInvoiceRouteSchema>;
export const StructureInvoiceCoverSchema = Type.Object(
  {
    kind: Type.Union([
      Type.Literal("internal"),
      Type.Literal("job-cost"),
      Type.Literal("job-supplier"),
    ]),
    id: uuid,
    amount,
  },
  { additionalProperties: false },
);
export type StructureInvoiceCover = Static<typeof StructureInvoiceCoverSchema>;
export const StructureInvoiceDecisionSchema = Type.Object(
  {
    invoiceId: uuid,
    action: Type.Union([Type.Literal("post"), Type.Literal("hold")]),
    costCenterId: Type.Optional(uuid),
    covers: Type.Optional(
      Type.Array(StructureInvoiceCoverSchema, { maxItems: 1000 }),
    ),
  },
  { additionalProperties: false },
);
export type StructureInvoiceIntegrationDecision = Static<
  typeof StructureInvoiceDecisionSchema
>;
export const StructureInvoiceAdjustmentSchema = Type.Object(
  {
    ...StructureInvoiceCoverSchema.properties,
    reason: Type.Union([Type.Literal("forecast"), Type.Literal("superseded")]),
    note: Type.String({ minLength: 5, maxLength: 1000 }),
  },
  { additionalProperties: false },
);
export type StructureInvoiceAdjustment = Static<
  typeof StructureInvoiceAdjustmentSchema
>;
export const StructureInvoiceIntegrationPreviewSchema = Type.Object(
  {
    managementYear: year,
    invoiceId: Type.Optional(uuid),
    routes: Type.Optional(
      Type.Array(StructureInvoiceRouteSchema, { maxItems: 500 }),
    ),
    decisions: Type.Optional(
      Type.Array(StructureInvoiceDecisionSchema, { maxItems: 1000 }),
    ),
    adjustments: Type.Optional(
      Type.Array(StructureInvoiceAdjustmentSchema, { maxItems: 1000 }),
    ),
  },
  { additionalProperties: false },
);
export type StructureInvoiceIntegrationPreviewInput = Static<
  typeof StructureInvoiceIntegrationPreviewSchema
>;
const write = {
  operationId: uuid,
  confirmed: Type.Literal(true),
  expectedFingerprint: Type.String({ pattern: "^[a-f0-9]{64}$" }),
};
export const StructureInvoiceIntegrationConfigureSchema = Type.Object(
  {
    managementYear: year,
    routes: Type.Array(StructureInvoiceRouteSchema, { maxItems: 500 }),
    enabled: Type.Boolean(),
    ...write,
  },
  { additionalProperties: false },
);
export type StructureInvoiceIntegrationConfigure = Static<
  typeof StructureInvoiceIntegrationConfigureSchema
>;
export const StructureInvoiceIntegrationReconcileSchema = Type.Object(
  {
    managementYear: year,
    invoiceId: Type.Optional(uuid),
    decisions: Type.Array(StructureInvoiceDecisionSchema, { maxItems: 1000 }),
    adjustments: Type.Optional(
      Type.Array(StructureInvoiceAdjustmentSchema, { maxItems: 1000 }),
    ),
    ...write,
  },
  { additionalProperties: false },
);
export type StructureInvoiceIntegrationReconcile = Static<
  typeof StructureInvoiceIntegrationReconcileSchema
>;
export interface StructureInvoiceManualSource {
  kind: StructureInvoiceCover["kind"];
  id: string;
  costCenterId: string;
  costCenterName: string;
  date: string;
  description: string | null;
  amount: string;
  remainingAmount: string;
  basis: "net" | "gross" | "unknown";
  aggregate: boolean;
  fingerprint: string;
}
export interface StructureInvoiceIntegrationCenter {
  id: string;
  name: string;
  available: boolean;
  previousAmount: string;
  manualAmount: string;
  laborAmount: string;
  ficAmount: string;
  coveredAmount: string;
  excludedAmount: string;
  nextAmount: string;
}
export interface StructureInvoiceIntegrationPreview {
  managementYear: number;
  enabled: boolean;
  fingerprint: string;
  routes: StructureInvoiceRoute[];
  centers: StructureInvoiceIntegrationCenter[];
  suppliers: { id: string; name: string }[];
  invoices: {
    id: string;
    supplierId: string | null;
    supplier: string;
    invoiceNumber: string | null;
    date: string;
    amount: string;
    grossAmount: string;
    category: string | null;
    costCenterId: string | null;
    costCenterName: string | null;
    destination?: "structure" | "job" | "mixed" | "review";
    status: "posted" | "held" | "missing-center" | "ineligible";
    reason: string;
    match: "existing" | "automatic" | "new" | "held";
    candidates: StructureInvoiceManualSource[];
    covers: StructureInvoiceCover[];
  }[];
  manualSources: StructureInvoiceManualSource[];
  adjustments: StructureInvoiceAdjustment[];
  totals: {
    previousAmount: string;
    manualAmount: string;
    laborAmount: string;
    ficAmount: string;
    coveredAmount: string;
    excludedAmount: string;
    nextAmount: string;
  };
  blockingIssues: string[];
}
export interface StructureInvoiceIntegrationResult {
  id: string;
  replayed: boolean;
  preview: StructureInvoiceIntegrationPreview;
}
